import { useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { EntityRow, formatCount, formatHours, formatWhen, sumMetrics, UsageMetricMeta } from "./usageTypes";

interface Column {
  key: string;
  label: string;
  value: (r: EntityRow) => number | null;
  format?: (n: number) => string;
  hint?: string;
}

interface Props {
  kind: "agency" | "officer";
  rows: EntityRow[];
  metrics: UsageMetricMeta[];
  onOpen: (row: EntityRow) => void;
  showHours: boolean;
}

const keysOf = (metrics: UsageMetricMeta[], category: string) => metrics.filter((m) => m.category === category).map((m) => m.key);

export function activityTotal(row: EntityRow, metrics: UsageMetricMeta[]): number {
  const access = new Set(keysOf(metrics, "access"));
  return Object.entries(row.metrics).reduce((a, [k, v]) => (access.has(k) ? a : a + v), 0);
}

export default function EntityTable({ kind, rows, metrics, onOpen, showHours }: Props) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<string>("actions");
  const [desc, setDesc] = useState(true);
  const [hideIdle, setHideIdle] = useState(false);

  const columns: Column[] = useMemo(() => {
    const cols: Column[] = [
      { key: "actions", label: "Actions", value: (r) => activityTotal(r, metrics), hint: "Every counted action, excluding sign-ins" },
      { key: "reports", label: "Reports", value: (r) => r.metrics.cases_created || 0 },
      { key: "address", label: "Address sugg.", value: (r) => r.metrics.suggestions_insert || 0, hint: "Address suggestions applied" },
      { key: "forms", label: "Forms", value: (r) => r.metrics.forms_filled || 0, hint: "Forms completed" },
      { key: "redactions", label: "Redactions", value: (r) => sumMetrics(r.metrics, keysOf(metrics, "redactions")) },
      { key: "investigations", label: "Investigations", value: (r) => sumMetrics(r.metrics, keysOf(metrics, "investigations")) },
      { key: "writing", label: "Writing tools", value: (r) => sumMetrics(r.metrics, keysOf(metrics, "writing")) },
      { key: "logins", label: "Sign-ins", value: (r) => r.metrics.logins || 0 },
    ];
    if (showHours) {
      cols.push({
        key: "hours",
        label: "Hours saved",
        value: (r) => (r.timeSaved ? r.timeSaved.personalHours : null),
        format: formatHours,
        hint: "Using each officer's own typing speed once measured",
      });
    }
    return cols;
  }, [metrics, showHours]);

  const sorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = rows.filter((r) => !q || r.name.toLowerCase().includes(q) || (r.sub || "").toLowerCase().includes(q));
    if (hideIdle) list = list.filter((r) => activityTotal(r, metrics) > 0 || (r.metrics.logins || 0) > 0);
    const col = columns.find((c) => c.key === sortKey);
    const val = (r: EntityRow) => {
      if (sortKey === "name") return r.name.toLowerCase();
      if (sortKey === "last") return r.lastActive || "";
      if (sortKey === "officers") return r.officers ?? 0;
      return col?.value(r) ?? -1;
    };
    return [...list].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const c = x < y ? -1 : x > y ? 1 : 0;
      return desc ? -c : c;
    });
  }, [rows, query, hideIdle, sortKey, desc, columns, metrics]);

  const head = (key: string, label: string, hint?: string, align = "text-right") => (
    <th
      key={key}
      scope="col"
      title={hint}
      aria-sort={sortKey === key ? (desc ? "descending" : "ascending") : "none"}
      className={cn("px-3 py-2 font-medium text-xs text-base-content/60 whitespace-nowrap select-none", align)}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-base-content"
        onClick={() => {
          if (sortKey === key) setDesc(!desc);
          else {
            setSortKey(key);
            setDesc(key !== "name");
          }
        }}
      >
        {label}
        {sortKey === key && (desc ? <ChevronDownIcon className="size-3" /> : <ChevronUpIcon className="size-3" />)}
      </button>
    </th>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative">
          <MagnifyingGlassIcon className="size-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/40" />
          <input
            className="input input-bordered input-sm bg-base-100 pl-8 w-64"
            placeholder={kind === "agency" ? "Search departments" : "Search officers"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-base-content/70 cursor-pointer">
          <input
            type="checkbox"
            className="checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500"
            checked={hideIdle}
            onChange={(e) => setHideIdle(e.target.checked)}
          />
          Hide inactive
        </label>
        <span className="text-xs text-base-content/50 ml-auto">
          {sorted.length} of {rows.length}
        </span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-base-300 bg-base-100">
        <table className="table table-sm w-full">
          <thead className="bg-base-200">
            <tr>
              {head("name", kind === "agency" ? "Department" : "Officer", undefined, "text-left")}
              {kind === "agency" && head("officers", "Officers")}
              {columns.map((c) => head(c.key, c.label, c.hint))}
              {head("last", "Last active")}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const idle = activityTotal(r, metrics) === 0;
              return (
                <tr
                  key={r.id}
                  tabIndex={0}
                  onClick={() => onOpen(r)}
                  onKeyDown={(e) => e.key === "Enter" && onOpen(r)}
                  className="hover cursor-pointer border-t border-base-200"
                >
                  <td className="px-3 py-2">
                    <p className={cn("m-0 text-sm font-medium", idle ? "text-base-content/50" : "text-base-content")}>{r.name}</p>
                    {r.sub && <p className="m-0 text-xs text-base-content/50">{r.sub}</p>}
                  </td>
                  {kind === "agency" && <td className="px-3 py-2 text-right text-sm tabular-nums">{r.officers ?? 0}</td>}
                  {columns.map((c) => {
                    const v = c.value(r);
                    return (
                      <td key={c.key} className={cn("px-3 py-2 text-right text-sm tabular-nums", v ? "text-base-content" : "text-base-content/30")}>
                        {v === null ? "-" : c.format ? c.format(v) : formatCount(v)}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right text-xs text-base-content/60 whitespace-nowrap">{formatWhen(r.lastActive)}</td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={columns.length + 3} className="px-3 py-8 text-center text-sm text-base-content/50">
                  Nothing matches.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
